import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import styles from "../../styles/shap_of_you.module.scss";
import shapesenco from "../../assets/images/shapesenco.png";
import { DesktopNavigationProps } from "./types";

const TermsConditionsScreen: React.FC<DesktopNavigationProps> = ({
  navigateToStep,
  isAuthenticated,
  logStep,
}) => {
  const [loading, setLoading] = useState(false);
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Check if user has scrolled to the bottom
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        // Check if scrolled to bottom (with 10px threshold for better UX)
        if (scrollTop + clientHeight >= scrollHeight - 10) {
          setHasScrolledToEnd(true);
        }
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener("scroll", handleScroll);
      // Check initial scroll position in case content is already at bottom
      handleScroll();
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // Handle "Agree and Proceed" button click
  const handleAgreeAndProceed = () => {
    setLoading(true);

    // Save consent acceptance to localStorage for all users
    // The API call will be made after login (in VerifyOtpScreen)
    localStorage.setItem("pendingConsentAcceptance", "true");

    // Log consent accept (non-blocking) - REMOVED: consent_accepted not in valid steps list
    // if (logStep) {
    //   logStep("consent_accepted");
    // }

    navigateToStep("welcome");
    setLoading(false);
  };

  // Handle "Don't Agree" button click
  const handleDontAgree = () => {
    // Log consent denied (non-blocking) - REMOVED: consent_rejected not in valid steps list
    // if (logStep) {
    //   logStep("consent_rejected");
    // }
    navigateToStep("permissionDenied");
  };

  return (
    <div className="d-none d-md-block w-100">
      <div className={styles.wrapper_tc_desktop}>
        {/* Header */}
        <div className={styles.wrapper_sec}>
          <header className={styles.header}>
            <Image src={shapesenco} alt="Logo" />
          </header>

          {/* Content */}
          <div className={styles.content} ref={contentRef}>
            <h2>CONSENT NOTICE</h2>

            <p>
              Senco Gold Limited (hereinafter referred to as “Company”, “We”,
              “Us”, or “Our”), incorporated under the Companies Act, 1956 (CIN:
              L36911WB1994PLC064637), with its registered office located at
              Diamond Prestige, 41A A.J.C. Bose Road, 10th Floor, Unit No. 1001,
              Kolkata – 700017, West Bengal, India, respects the privacy of any
              person who accesses or uses Our Website/Application (hereinafter
              referred to as “You” or “Your”) and is committed to protecting
              your Digital Personal Data. This notice applies to the processing
              of Personal Data collected in digital form or any non-digital form
              which is digitised subsequently.{" "}
            </p>
            <p>
              In order to comply with the Digital Personal Data Protection Act,
              2023 (“DPDP Act”) and the rules framed thereunder and to nurture
              Our association, we would be collecting and processing Your
              Digital Personal Data. For this data collection and its
              processing, we need Your explicit consent.{" "}
            </p>
            <p>
              The purpose of processing Your Digital Personal Data is to support
              the services provided by the Company or its affiliates to You
              (please visit Our website for a list of services offered by the
              Company) or to facilitate Your interactions with the Company in
              roles, including but not limited to, an employee or
              vendor/supplier. There may be instances where Your Digital
              Personal Data needs to be shared with regulators, business
              associates, or payment gateways, resulting in Your data being
              transferred beyond Company’s control. This sharing may also be
              necessitated now or in future as per the requirement and in
              accordance with the applicable laws/regulations in India, as may
              be amended from time to time.{" "}
            </p>
            <p>
              You reserve the right to file a complaint with the Company
              regarding any grievances arising from the processing of Your
              Digital Personal Data, which shall be directed to the Grievance
              Officer, as detailed in Our Policy. Should the grievance remain
              unresolved through Our grievance redressal mechanism, You further
              retain the right to approach the Data Protection Board of India
              with a complaint, as outlined in the provisions of the DPDP Act.{" "}
            </p>
            <p>
              All the capitalised terms used in this Consent Notice shall have
              the same meaning assigned to them as in Company’s Policy.
              (“Policy”).{" "}
            </p>
            <div className={styles.heading}>
              Declaration of Understanding - Data Processing Policies:
            </div>
            <p>
              <strong>1</strong>. I understand that my Digital Personal Data may
              be collected only with my valid consent, and that it may be
              processed only in accordance with the Policy.
            </p>
            <p>
              <strong>2</strong>. I agree to provide the following information
              to Company during registration on our App/ Website or usage of our
              App/ Website or both, as and when necessary, including, but not
              limited to:
            </p>
            <ul className={styles.list_two_liv}>
              <li>
                <strong className="pb-2 d-block">Your information:</strong>
                <ul className={styles.list_two_liv}>
                  <li>Name</li>
                  <li>Gender</li>
                  <li>Occupation</li>
                  <li>Education</li>
                  <li>Address</li>
                  <li>Financial information</li>
                  <li>
                    Identity proof such as PAN Card, AADHAR Card, Voter ID card
                    etc.
                  </li>
                  <li>Previous purchase history from Us</li>
                  <li>Consumer durables owned</li>
                </ul>
              </li>
              <li>
                <strong className="pb-2 pt-2 d-block">
                  Communication details:
                </strong>
                <ul className={styles.list_two_liv}>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Postal address</li>
                  <li>Any other contact information</li>
                </ul>
              </li>
              <li>
                <strong className="pb-2 pt-2 d-block">
                  Device information:
                </strong>
                <ul className={styles.list_two_liv}>
                  <li>Operating system</li>
                  <li>Operating system providing advertising ID</li>
                  <li>IP address</li>
                  <li>Date and time of every user request</li>
                  <li>
                    Your usage information with Our devices placed at Our office
                  </li>
                  <li>Your internet protocol address</li>
                </ul>
              </li>
              <li>
                <strong className="pb-2 pt-2 d-block">
                  Behavioural patterns:
                </strong>
                <ul className={styles.list_two_liv}>
                  <li>Your active seconds and minutes in Our work database</li>
                  <li>
                    Your search history in Our device located at Our office
                  </li>
                  <li>
                    Sharing a product or inspiration to social media sites
                  </li>
                  <li>
                    Other interactions and dialogues during interactive search
                  </li>
                </ul>
              </li>
              <li>
                <strong className="pb-2 pt-2 d-block">
                  Non-personal information (THIS TYPE OF INFORMATION DOES NOT
                  IDENTIFY YOU PERSONALLY)
                </strong>
                <ul className={styles.list_two_liv}>
                  <li>The pages you visit</li>
                  <li>Links you click</li>
                  <li>Advertisements you view</li>
                  <li>Number of times you access Our page</li>
                  <li>
                    Your opinion on Our services, products, features that We
                    offer
                  </li>
                  <li>Operating system</li>
                  <li>Browser type</li>
                  <li>Internet service provider</li>
                  <li>Aggregate user data</li>
                  <li>Software and hardware attributes</li>
                  <li>Number of clicks</li>
                </ul>
              </li>
            </ul>

            <p>
              {" "}
              If You communicate with Us, by, for example, email or letter, any
              information provided in such communication may be collected and
              stored by Us.
            </p>
            <p>
              <strong>3</strong>. Specifically, You understand that Your Digital
              Personal Data may be processed for any or all of the following
              purposes, as may be applicable:
            </p>
            <div className="ps-2">
              <p>
                (i) Facilitating personnel management, including verification,
                compensation management (such as bank details for salary
                disbursement), performance management, compliance with legal
                obligations, health and safety management, training and
                development, and internal communications.
              </p>
              <p>
                (ii) Fulfilling customer orders placed, including but not
                limited to, managing payment transactions, coordinating shipping
                logistics, and facilitating the delivery of goods.
              </p>
              <p>
                (iii) Facilitating communication regarding order confirmations,
                invoices, and receipts for purchases made both online and
                offline.
              </p>
              <p>
                (iv) Communicating updates pertaining to the status of orders or
                delivery schedules.
              </p>
              <p>
                (v) Managing inquiries, complaints, or requests for assistance.
              </p>
              <p>
                (vi) Disseminating newsletters and promotional communications
                regarding upcoming events, sales, and the introduction of new
                products.
              </p>
              <p>
                (vii) Soliciting customer feedback through surveys and rating
                mechanisms.
              </p>
              <p>
                (viii) Enrolling in loyalty or rewards programs and thereby
                tracking points or rewards accumulated through purchases made
                both online and offline.
              </p>
              <p>
                (ix) Complying with applicable legal obligations, including but
                not limited to tax reporting, audit requirements, or responses
                to requests from law enforcement authorities.
              </p>
              <p>
                (x) Analysing market trends, conducting market research, and
                improving overall business strategies, utilizing information
                gathered from both online and offline sales.
              </p>
              <p>
                (xi) Detecting and preventing fraudulent transactions or
                unauthorized access to customer accounts, thereby safeguarding
                both the customer and the brand across all sales channels.
              </p>
              <p>
                (xii) Sharing information with third-party service providers for
                limited purposes of operational and logistical functions to
                complete the order.
              </p>
              <p>
                (xiii) Facilitating linking of customer accounts with social
                media platforms for the purpose of sharing purchases or
                promotional materials, whether derived from online or offline
                transactions.
              </p>
              <p>
                (xiv) Facilitating effective communication, assessing service
                quality, and managing supply chain operations for purposes
                related to the business relationship, including but not limited
                to entering into and managing agreements, evaluating supplier
                qualifications, processing payments, and ensuring compliance
                with contractual obligations.
              </p>
              <p>(xv) To build up marketing profiles.</p>
              <p>
                (xvi) Aiding strategic development, data collection, and
                business analytics.
              </p>
              <p>
                (xvii) Managing our relationship with advertisers and other
                partners.
              </p>
              <p>(xviii) Any other grounds as per applicable law.</p>
            </div>
            <p>
              {" "}
              <strong>4</strong>. I understand that I have certain rights that I
              enjoy as a data principal over my Digital Personal Data, in
              relation to its collection, use and processing. These rights,
              inter alia, relate to confirmation and access, correction and
              erasure, objection of processing, grievance redressal and
              withdrawal of consent. These rights are available to me in the
              manner set out in the Policy.
            </p>
            <p>
              <strong>5</strong>. I agree that am of lawful age to contract as
              per applicable laws and I have read and understood the processing
              requirement with respect to my Digital Personal Data as listed
              above and provide my free, specific, informed, unconditional and
              unambiguous consent to the above by signing on the space provided
              below.
            </p>
            <p>
              <strong>6</strong>. I understand that I have the right to withdraw
              my consent at any time in the manner specified under the Policy.
              If I need to withdraw my consent with respect to usage of my
              Digital Personal Data under Company’s purview, excluding data
              which has already been shared with the authorities, regulators or
              business associates and outside the control of the Company at that
              time, I will inform the Company through the{" "}
              <Link href="/account/profile" style={{ color: "rgb(147, 16, 29)" }}>
                Consent Withdrawal Form
              </Link>{" "}
              as provided on the Website.{" "}
            </p>
            <p>
              <strong>7</strong>. I understand that this withdrawal of consent
              will be implemented as soon as practicable. I also confirm that
              the Company, while accepting my request for withdrawal of consent,
              will be allowed to keep such Digital Personal Data as required by
              the applicable laws and regulations for history and record keeping
              purposes for a specified period of time.{" "}
            </p>
            <p>
              <strong>8</strong>. By clicking on “I agree to consent to the
              processing of my Digital Personal Data” below, I signify agreement
              to the processing and sharing of my Digital Personal Data with the
              Company now and in future in order to allow the Company to conduct
              business or sharing information with business associates or
              authorities as may be required under different regulations, laws
              and statues of India and outside India, or with such third
              parties/vendors, as may be applicable and amended from time to
              time.
            </p>
          </div>
        </div>
        {/* CTA */}
        <div className={styles.cta_btn_container}>
          <div className={styles.btn_act}>
            <button
              className={styles.ctaBtn1}
              onClick={handleDontAgree}
              disabled={loading || !hasScrolledToEnd}
              style={
                !hasScrolledToEnd || loading
                  ? {
                      background: "#d3d3d3",
                      color: "#888",
                      cursor: "not-allowed",
                      opacity: 0.6,
                    }
                  : {}
              }
            >
              Don&apos;t Agree
            </button>
            <button
              className={styles.ctaBtn}
              onClick={handleAgreeAndProceed}
              disabled={loading || !hasScrolledToEnd}
              style={
                loading || !hasScrolledToEnd
                  ? {
                      background: "#d3d3d3",
                      color: "#888",
                      cursor: "not-allowed",
                      opacity: 0.6,
                    }
                  : {}
              }
            >
              {loading ? "Processing..." : "Agree and Proceed"} →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditionsScreen;
